import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from 'core/api/queryKeys';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { userApi } from 'features/home/home.api';
import { EmptyState, ErrorState, Skeleton, StatusChip } from 'shared/components/Feedback';
import { PageMeta } from 'shared/components/PageMeta';
import { Pagination } from 'shared/components/Pagination';
import { asList, formatDateTime, localizedText } from 'shared/utilities/format';

export function SupportPage() {
  const { t, locale } = useI18n();
  const [page, setPage] = useState(1);
  const categoriesQuery = useQuery({
    queryKey: queryKeys.supportCategories,
    queryFn: () => userApi.supportCategories(),
  });
  const ticketsQuery = useQuery({
    queryKey: queryKeys.tickets(page),
    queryFn: () => userApi.tickets({ page, per_page: 15 }),
  });

  if (categoriesQuery.isLoading || ticketsQuery.isLoading) {
    return <Skeleton rows={6} />;
  }
  if (categoriesQuery.isError) {
    return <ErrorState title={humanizeApiError(categoriesQuery.error, t)} onRetry={() => categoriesQuery.refetch()} retryLabel={t('actions.retry')} />;
  }

  const categories = asList(categoriesQuery.data);
  const tickets = ticketsQuery.data?.items || [];

  return (
    <div className="list-page">
      <PageMeta titleKey="meta.support" />
      <header className="page-header">
        <h1>{t('support.title')}</h1>
        <Link className="btn btn-primary" to={paths.supportNew}>{t('support.newTitle')}</Link>
      </header>
      <h2>{t('support.categories')}</h2>
      {categories.length === 0 ? <EmptyState title={t('support.emptyCategories')} /> : (
        <div className="chip-row">
          {categories.map((category) => (
            <StatusChip key={category.id}>{localizedText(category, locale, ['title', 'name'])}</StatusChip>
          ))}
        </div>
      )}
      <h2>{t('support.tickets')}</h2>
      {ticketsQuery.isError ? (
        <ErrorState title={humanizeApiError(ticketsQuery.error, t)} onRetry={() => ticketsQuery.refetch()} retryLabel={t('actions.retry')} />
      ) : tickets.length === 0 ? (
        <EmptyState title={t('support.emptyTickets')} />
      ) : (
        <div className="record-table">
          <div className="record-table-head">
            <span>{t('consultations.referenceLabel')}</span>
            <span>{t('support.category')}</span>
            <span>{t('support.message')}</span>
            <span />
            <span />
          </div>
          {tickets.map((ticket) => (
            <article key={ticket.id} className="record-row">
              <div>
                <strong>{ticket.reference}</strong>
                <StatusChip tone={ticket.status}>{ticket.status_label || t(`support.statuses.${ticket.status}`)}</StatusChip>
              </div>
              <p>{localizedText(ticket.category, locale, ['title', 'name'])}</p>
              <p>{ticket.message}</p>
              <p className="muted">{formatDateTime(ticket.created_at, locale)}</p>
              <Link className="btn btn-secondary" to={paths.supportTicket(ticket.id)}>{t('actions.view')}</Link>
            </article>
          ))}
        </div>
      )}
      <Pagination meta={ticketsQuery.data?.meta} page={page} onPageChange={setPage} />
    </div>
  );
}
